/*eslint max-lines-per-function: ["warn", {"max": 275, "skipBlankLines": true,"skipComments": true }]*/

import type {
    DocumentEntityInterface,
    DataInstance,
} from "@busy-human/gearbox";
import {useAppState} from "./useAppState";
import {Leads} from "@busy-human/k9-library";
import { defineStore, StoreDefinition } from "pinia";
import { computed, ref } from "vue";

// DocumentEntityInterface<Leads.Model>.$id: string
export type LeadsStore = StoreDefinition<"leads", any>;
export type LeadModel  = Leads.Model;
export type LeadInst   = DataInstance<Leads.Model>;
export type LeadDoc    = DocumentEntityInterface<Leads.Model>;
export type Lead       = Partial<LeadModel> & Partial<LeadInst> & Partial<LeadDoc>;
export type LeadRef    = {[id: string]: Lead};
export type LeadsList  = Array<Lead>;
export type LeadsMap   = {[key: string]: Lead};

/**
 * --- STORE ---
 * Pinia "Setup" store:
 * ref()s become state properties,
 * computed()s become getters,
 * function()s become actions;
*/

export const useLeads:LeadsStore = defineStore('leads', () => {

    const appState     = useAppState();
    const {
        setLoading,
        setFinished,
    } = appState;

    /* - State - ref()s become state properties */
    const selectedItem = ref<Lead>(Leads.Defaults());
    const leadsList    = ref<LeadsList>([]);
    const leadsMap     = ref<LeadsMap>({});
    const selectedId   = ref("");
    const itemDirty    = ref(false);
    const leadsHidden  = ref(false);
    const activeToggleMain = ref(true); // Default sorting by Date
    const activeToggleThree = ref(false); // Default sorting by Ascending

    /* - Getters - computed()s become getters */
    const getLeadsList = computed<LeadsList>(() => Object.values(leadsList.value));
    const getFirstNameString = computed(() => selectedItem.value.name?.firstName);
    const getLastNameString = computed(() => selectedItem.value.name?.lastName);
    const getFullNameString = computed(() =>
        selectedItem.value.name?.fullName ||
        `${selectedItem.value.name?.firstName} ${selectedItem.value.name?.lastName}`
    );

    /** - Actions - function()s become actions */

    /** Action: retrieve List from Leads Collection*/
    function setLeadsList(newLeads: Lead[]) {
        leadsList.value = newLeads;
    }

    async function fetchLeads() {
        setLoading();
        let result;
        try{
            result = await Leads.Collection.fetchAll();
            const idObj = {} as LeadsMap;
            const list  = [];
            for (const i of result.items) {
                const item = Object.assign(i.data(), {_id: i.$id});
                idObj[i.$id as string] = item;
                if (item.status !== 'Hidden') {
                    list.unshift(item);
                }
            }
            leadsList.value = list;
            leadsMap.value  = idObj;
            leadsHidden.value = true;
            setFinished();
            return {success: true, message: ""};
        } catch (err) {
            setFinished();
            return {success: false, message: `Error in "fetchLeads" function, with err msg:\n${err}`};
        }
    }
    fetchLeads();

    /** Shows leads with status of hidden from leads collection */
    async function showHidden() {
        setLoading();
        let result;
        try{
            result = await Leads.Collection.fetchAll();
            const idObj = {} as LeadsMap;
            const list  = [];
            for (const i of result.items) {
                const item = Object.assign(i.data(), {_id: i.$id});
                idObj[i.$id as string] = item;
                list.unshift(item);
            }
            leadsList.value = list;
            leadsMap.value  = idObj;
            leadsHidden.value = !leadsHidden.value;
            setFinished();
            return {success: true, message: ""};
        } catch (err) {
            setFinished();
            return {success: false, message: `Error in "fetchLeads" function, with err msg:\n${err}`};
        }
    }
    function toggleHiddenLeads(){
        if(leadsHidden.value) {
            showHidden();
            console.log("LEADS", leadsHidden.value);
        } else {
            fetchLeads();
        }
    }

    /** Update selectedItem's info, from editing in detail component  */
    function updateSelectedItem(item: Lead ) {
        setLoading();
        selectedItem.value = { ...selectedItem.value, ...item};
        if(selectedItem.value._id) leadsMap.value[selectedItem.value._id] = selectedItem.value;
        setFinished();
    }

    /** Select an Item  */
    const selectItem = (item: Lead )=>{
        // console.log("SELECT ITEM IN USE LEADS: ", item )
        if( (item === undefined) || (item === null) ) return;
        selectedItem.value = item;
        selectedId.value = item._id as string;
    };

    function selectFirstItem (){
        selectItem(leadsList.value[0]);
    }

    /** Add lead provided to database and local store */
    async function addNewLead(lead: LeadModel) {
        const newLead = await Leads.Collection.add(lead);
        leadsList.value.unshift(newLead);
        leadsMap.value[newLead.$id] = newLead;
        selectItem(newLead);
        setFinished();
    }

    function createNewTempLead(){
        const tempLead: Lead = Leads.Defaults();
        const tempID = `temp_${Date.now()}`;
        tempLead._id = tempID;
        leadsMap.value[tempID] = tempLead;
        leadsList.value.unshift(tempLead);
        selectItem(tempLead);
        setFinished();
    }

    async function saveItem(){
        let response:{success: boolean, reason: any} = {success: false, reason: null};
        if(!selectedItem?.value?._id){
            response = {success: false, reason: "Cannot save lead; No id on item"};
            return;
        };
        setLoading();
        /* Delete temp_ id info */
        if (selectedItem.value._id?.includes("temp_")) selectedItem.value._id = "";
        /* Ensure _id has any string as its value */
        const _id:string = selectedItem.value._id || selectedItem.value.$id || "";
        /* Do not save $id to Firebase */
        if(Object.hasOwn(selectedItem.value,"$id")) delete selectedItem.value.$id;

        if(_id === "" || _id.length < 2){
            console.info(" Create new Lead Doc ");
            try{
                const newLead = await Leads.Collection.add(selectedItem.value);
                leadsMap.value[newLead.$id] = newLead.data();
                response = { success: true, reason: null };
            } catch (e) {
                console.error("Tried to save the Lead, but got an error.\n Error: \n ", e);
                response = {success: false, reason: e, };
            }
        } else if( _id.length > 1) {
            console.info(" Updating existing Lead Doc ");
            try{
                await Leads.Collection.updateDoc(_id,selectedItem.value);
                response = { success: true, reason: null };
            } catch (e) {
                console.error(e);
                response = {success: false, reason: e, };
            }
        } else {
            console.warn(" Item had no _id key ?! ", selectedItem.value);
        }
        fetchLeads();
        setFinished();
        return response;
    };

    async function saveByID(id: string ) {
        setLoading();
        const item = leadsMap.value[id];
        try{
            await Leads.Collection.updateDoc(id, item);
            // leads.value[newLead.$id] = newLead.data()
            setFinished();
            return { success: true, reason: null };
        } catch (e) {
            console.error(e);
            setFinished();
            return {success: false, reason: e, };
        }
    };

    function setFullName (name: Leads.Model["name"]) {
        console.log( " NAME: ", name);
        const fullName = `${(name && name.firstName) || ""} ${(name && name.lastName) || ""}`;
        return fullName;
    }

    /** Check if item is dirty; True == Dirty, False == Pristine; */
    function isItemDirty(currentItem:LeadModel){
        const somethingChanged = (
            (currentItem.notes === selectedItem.value.notes) &&
            (currentItem.status === selectedItem.value.status) &&
            (currentItem.productInterest === selectedItem.value.productInterest) &&
            (currentItem.email === selectedItem.value.email) &&
            (currentItem.phone === selectedItem.value.phone) &&
            (currentItem.lastContact === selectedItem.value.lastContact) &&
            (currentItem.name?.firstName === selectedItem?.value?.name?.firstName) &&
            (currentItem.name?.lastName === selectedItem?.value?.name?.lastName) &&
            (selectedItem?.value?.name?.fullName.length < 2) &&
            (currentItem.name?.fullName === selectedItem?.value?.name?.fullName)
        );

        if (currentItem.name && currentItem.name.fullName.length < 2) {
            currentItem.name.fullName = setFullName(currentItem.name);
            console.log("Full name was empty? ", selectedItem.value.name?.fullName, currentItem.name?.fullName);
        }

        // console.log("CURRENT ITEM:", selectedItem.value.lastContact, currentItem.lastContact,currentItem.name?.fullName,selectedItem?.value?.name?.fullName);
        // console.info( " -- Checkitem - Any change? : ", somethingChanged );
        itemDirty.value = somethingChanged;
        return somethingChanged;
    };

    const isItemEmpty = (currentItem:LeadModel) => ((
            currentItem.name?.firstName + currentItem.name?.lastName +
            currentItem.notes + currentItem.email + currentItem.phone
        ).length < 2
    );

    async function deleteEmptyItem(currentItem:LeadModel){
        let feedback = {
            success:false,
            reason: {}
        };
        console.log();
        if(!currentItem._id){
            console.log( " Cannot delete an item with no _id");
            return setFinished();
        } else if(isItemEmpty(currentItem)){
            setLoading();
            console.log( " ITEM ID to delete: ", currentItem, selectedId, selectedItem);
            await Leads.Collection.deleteDoc(currentItem._id).then(
                (res:any) => {feedback = {success:true,reason:res};}
            ).catch(
                (error:any) => {feedback = {success:false,reason:error};}
            );
            setFinished();
            return feedback;
        } else {
            feedback = {
                success:true,
                reason: {message: "Item was not empty"}
            };
            setFinished();
            return feedback;
        }
    }

    return {
        /* State properties */
        selectedItem,
        leadsList,
        leadsMap,
        selectedId,
        itemDirty,
        leadsHidden,
        activeToggleMain,
        activeToggleThree,

        /* Getters */
        getLeadsList,
        getFirstNameString,
        getFullNameString,
        getLastNameString,

        /* Actions */
        setLeadsList,
        fetchLeads,

        showHidden,
        toggleHiddenLeads,

        updateSelectedItem,
        selectItem,
        selectFirstItem,

        addNewLead,
        createNewTempLead,

        saveItem,
        saveByID,

        isItemDirty,
        isItemEmpty,
        deleteEmptyItem,
    };

});